import React, { useCallback, useMemo } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Field, Form } from 'react-final-form';
import { Button, Loader } from 'ncoded-component-library';
import TextAreaField from 'components/TextAreaField';
import validations from 'validations';
import { getUserFullName } from 'utils';
import TransWithNda from 'components/TransWithNda';

import './AutomaticMessageForm.styles.scss';
import './AutomaticMessageForm.styles.responsive.scss';

type FormValues = {
  message: string;
};

type AutomaticMessageFormProps = {
  className?: string;
  leadFirstName: string;
  leadLastName: string;
  envelopeTemplateName: string;
  onCancel: () => void;
  onSendNda: (message?: string) => void;
};

const AutomaticMessageForm: React.FC<AutomaticMessageFormProps> = (props) => {
  const {
    className,
    leadFirstName,
    leadLastName,
    envelopeTemplateName,
    onCancel,
    onSendNda,
  } = props;

  const leadFullName = useMemo(
    () => getUserFullName(leadFirstName, leadLastName),
    [leadFirstName, leadLastName],
  );

  const classes = classNames('automatic-message-form', className);

  const { t } = useTranslation();

  const initialValues = useMemo(
    () => ({
      message: `Hi ${leadFirstName},
We have sent you a Non-Disclosure Agreement.
You can access via the link on the right hand side. 

Thank you`,
    }),
    [leadFirstName],
  );

  const onSubmit = useCallback(
    ({ message }: FormValues) => {
      onSendNda(message);
    },
    [onSendNda],
  );

  return (
    <Form
      onSubmit={onSubmit}
      initialValues={initialValues}
      render={({ submitting, invalid, handleSubmit }) => (
        <form onSubmit={handleSubmit} className={classes}>
          {submitting && <Loader />}

          <TransWithNda
            i18nKey="sendingNDADescription"
            values={{ leadFullName }}
            envelopeTemplateName={envelopeTemplateName}
          />

          <Field
            name="message"
            component={TextAreaField}
            rows={6}
            required
            validate={validations.required()}
          />

          <div className="actions">
            <Button variant="link" onClick={onCancel}>
              {t('cancel')}
            </Button>

            <div>
              <Button variant="outline" onClick={() => onSendNda()}>
                {t('sendNdaWithoutMessage')}
              </Button>

              <Button type="submit" disabled={submitting || invalid}>
                {t('sendNdaWithMessage')}
              </Button>
            </div>
          </div>
        </form>
      )}
    />
  );
};

export default AutomaticMessageForm;
